// ═══════════════════════════════════════════════════════════════════════════════
// Limit-reached error detection & modal bridge (§22)
// ═══════════════════════════════════════════════════════════════════════════════

import { LIMIT_ERROR_CODE, USER_LEVEL, type LimitErrorCode, type TotalPinMessageUsageSnapshot, type TotalTaskUsageSnapshot } from '@fdh/shared';
import { ApiClientError } from './api';

const LIMIT_CODES = new Set<string>(Object.values(LIMIT_ERROR_CODE));

export type LimitBatchQuotaIssue = {
  code: LimitErrorCode;
  dailyLimit: number;
  usedToday: number;
  resetAt: string;
};

export type LimitReachedPayload = {
  code: LimitErrorCode;
  dailyLimit?: number;
  usedToday?: number;
  maxTotalTasks?: number | null;
  maxPinMessages?: number;
  maxComments?: number;
  total?: number;
  retryAt?: string;
  /** Batch pre-check: how many items user tried to process. */
  requestedCount?: number;
  /** AI batch wording: receipt images vs stocktake photos. */
  batchVariant?: 'receipt' | 'photo';
  /** Multiple daily quotas insufficient — show in one modal. */
  batchIssues?: LimitBatchQuotaIssue[];
};

export const isLimitErrorCode = (code: string | undefined): code is LimitErrorCode =>
  typeof code === 'string' && LIMIT_CODES.has(code);

export const isTotalTaskLimitReached = (
  totalTasks: TotalTaskUsageSnapshot | undefined,
): totalTasks is TotalTaskUsageSnapshot => {
  if (!totalTasks) {
    return false;
  }
  if (totalTasks.maxTotalTasks === null) {
    return false;
  }
  return totalTasks.usedTotal >= totalTasks.maxTotalTasks;
};

export const buildTotalTaskLimitPayload = (
  totalTasks: TotalTaskUsageSnapshot,
): LimitReachedPayload => ({
  code: LIMIT_ERROR_CODE.TOTAL_TASK_LIMIT_REACHED,
  total: totalTasks.usedTotal,
  maxTotalTasks: totalTasks.maxTotalTasks,
});

/** Show task-kind not allowed modal (e.g. L2 employer tapping locked task types). */
export const emitTaskKindNotAllowed = (): void => {
  emitLimitReached({ code: LIMIT_ERROR_CODE.TASK_KIND_NOT_ALLOWED });
};

/** Show total-task limit modal when at cap; returns true if blocked. */
export const emitTotalTaskLimitIfReached = (
  totalTasks: TotalTaskUsageSnapshot | undefined,
): boolean => {
  if (!isTotalTaskLimitReached(totalTasks)) {
    return false;
  }
  emitLimitReached(buildTotalTaskLimitPayload(totalTasks));
  return true;
};

export const isPinMessageLimitReached = (
  pinMessages: TotalPinMessageUsageSnapshot | undefined,
): pinMessages is TotalPinMessageUsageSnapshot => {
  if (!pinMessages) {
    return false;
  }
  return pinMessages.usedTotal >= pinMessages.maxPinMessages;
};

export const buildPinMessageLimitPayload = (
  pinMessages: TotalPinMessageUsageSnapshot,
): LimitReachedPayload => ({
  code: LIMIT_ERROR_CODE.PIN_MESSAGE_LIMIT_REACHED,
  total: pinMessages.usedTotal,
  maxPinMessages: pinMessages.maxPinMessages,
});

/** Show pin-message limit modal when at cap; returns true if blocked. */
export const emitPinMessageLimitIfReached = (
  pinMessages: TotalPinMessageUsageSnapshot | undefined,
): boolean => {
  if (!isPinMessageLimitReached(pinMessages)) {
    return false;
  }
  emitLimitReached(buildPinMessageLimitPayload(pinMessages));
  return true;
};

/** L2 over total cap: block inactive→active when active slots are full. */
export const emitPinMessageActiveLimitIfReached = (
  activeCount: number,
  maxActive: number,
  totalCount: number,
  level: number,
): boolean => {
  if (level !== USER_LEVEL.FREE || totalCount <= maxActive || activeCount < maxActive) {
    return false;
  }
  emitLimitReached({
    code: LIMIT_ERROR_CODE.PIN_MESSAGE_ACTIVE_LIMIT_REACHED,
    total: activeCount,
    maxPinMessages: maxActive,
  });
  return true;
};

export const emitCommentLimitIfReached = (
  usedComments: number,
  maxComments: number,
): boolean => {
  if (maxComments < 1 || usedComments < maxComments) {
    return false;
  }
  emitLimitReached({
    code: LIMIT_ERROR_CODE.COMMENT_LIMIT_REACHED,
    total: usedComments,
    maxComments,
  });
  return true;
};

export const emitInventoryMetadataLimitIfReached = (
  code:
    | typeof LIMIT_ERROR_CODE.INVENTORY_LOCATION_LIMIT_REACHED
    | typeof LIMIT_ERROR_CODE.INVENTORY_CATEGORY_LIMIT_REACHED,
  usedTotal: number,
  maxAllowed: number | null | undefined,
): boolean => {
  if (maxAllowed === null || maxAllowed === undefined || maxAllowed < 1) {
    return false;
  }
  if (usedTotal < maxAllowed) {
    return false;
  }
  emitLimitReached({
    code,
    total: usedTotal,
    maxComments: maxAllowed,
  });
  return true;
};

export type DailyUsageSnapshotLike = {
  dailyLimit: number;
  usedToday: number;
  resetAt: string;
};

export const isDailyQuotaInsufficient = (
  usage: DailyUsageSnapshotLike | undefined,
  requestedCount: number,
): usage is DailyUsageSnapshotLike => {
  if (!usage || requestedCount < 1) {
    return false;
  }
  return usage.usedToday + requestedCount > usage.dailyLimit;
};

export const resolveBatchInsufficientBodyKey = (
  issueCode: LimitErrorCode,
  batchVariant?: 'receipt' | 'photo',
): string => {
  if (issueCode === LIMIT_ERROR_CODE.DAILY_RECEIPT_LIMIT_REACHED) {
    return 'limitModal.body.batchInsufficient.DAILY_RECEIPT_LIMIT_REACHED';
  }
  if (issueCode === LIMIT_ERROR_CODE.DAILY_AI_ANALYZE_LIMIT_REACHED) {
    return batchVariant === 'photo'
      ? 'limitModal.body.batchInsufficient.DAILY_AI_ANALYZE_PHOTO'
      : 'limitModal.body.batchInsufficient.DAILY_AI_ANALYZE_RECEIPT';
  }
  return `limitModal.body.${issueCode}`;
};

type BatchQuotaUsageInput = {
  ai?: DailyUsageSnapshotLike;
  receipt?: DailyUsageSnapshotLike;
};

type EmitBatchQuotaOptions = {
  checkAi?: boolean;
  checkReceipt?: boolean;
  batchVariant?: 'receipt' | 'photo';
};

/** Show one modal listing all insufficient daily quotas; returns true if blocked. */
export const emitBatchQuotaLimitIfInsufficient = (
  usage: BatchQuotaUsageInput,
  requestedCount: number,
  options: EmitBatchQuotaOptions = {},
): boolean => {
  const checkAi = options.checkAi === true;
  const checkReceipt = options.checkReceipt === true;
  const batchVariant = options.batchVariant ?? 'receipt';
  const issues: LimitBatchQuotaIssue[] = [];

  if (checkAi && isDailyQuotaInsufficient(usage.ai, requestedCount)) {
    issues.push({
      code: LIMIT_ERROR_CODE.DAILY_AI_ANALYZE_LIMIT_REACHED,
      dailyLimit: usage.ai.dailyLimit,
      usedToday: usage.ai.usedToday,
      resetAt: usage.ai.resetAt,
    });
  }
  if (checkReceipt && isDailyQuotaInsufficient(usage.receipt, requestedCount)) {
    issues.push({
      code: LIMIT_ERROR_CODE.DAILY_RECEIPT_LIMIT_REACHED,
      dailyLimit: usage.receipt.dailyLimit,
      usedToday: usage.receipt.usedToday,
      resetAt: usage.receipt.resetAt,
    });
  }

  if (issues.length === 0) {
    return false;
  }

  const primary = issues[0];
  emitLimitReached({
    code: primary.code,
    dailyLimit: primary.dailyLimit,
    usedToday: primary.usedToday,
    retryAt: primary.resetAt,
    requestedCount,
    batchVariant,
    batchIssues: issues,
  });
  return true;
};

/** Receipt scan — check AI + receipt submit quotas before upload/analyze. */
export const emitReceiptScanUploadQuotaLimitIfInsufficient = (
  usage: BatchQuotaUsageInput,
  requestedCount: number,
): boolean =>
  emitBatchQuotaLimitIfInsufficient(usage, requestedCount, {
    checkAi: true,
    checkReceipt: true,
    batchVariant: 'receipt',
  });

/** Receipt submit — check receipt submit quota only (e.g. batch submit without new upload). */
export const emitReceiptSubmitQuotaLimitIfInsufficient = (
  receipt: DailyUsageSnapshotLike | undefined,
  requestedCount: number,
): boolean =>
  emitBatchQuotaLimitIfInsufficient({ receipt }, requestedCount, {
    checkReceipt: true,
    batchVariant: 'receipt',
  });

/** Show daily-quota limit modal when batch would exceed cap; returns true if blocked. */
export const emitDailyQuotaLimitIfInsufficient = (
  usage: DailyUsageSnapshotLike | undefined,
  requestedCount: number,
  code: LimitErrorCode,
  batchVariant?: 'receipt' | 'photo',
): boolean => {
  if (code === LIMIT_ERROR_CODE.DAILY_AI_ANALYZE_LIMIT_REACHED) {
    return emitBatchQuotaLimitIfInsufficient({ ai: usage }, requestedCount, {
      checkAi: true,
      batchVariant: batchVariant ?? 'receipt',
    });
  }
  if (code === LIMIT_ERROR_CODE.DAILY_RECEIPT_LIMIT_REACHED) {
    return emitBatchQuotaLimitIfInsufficient({ receipt: usage }, requestedCount, {
      checkReceipt: true,
      batchVariant: 'receipt',
    });
  }
  if (!isDailyQuotaInsufficient(usage, requestedCount)) {
    return false;
  }
  emitLimitReached({
    code,
    dailyLimit: usage.dailyLimit,
    usedToday: usage.usedToday,
    retryAt: usage.resetAt,
    requestedCount,
    batchVariant,
    batchIssues: [
      {
        code,
        dailyLimit: usage.dailyLimit,
        usedToday: usage.usedToday,
        resetAt: usage.resetAt,
      },
    ],
  });
  return true;
};

export const parseLimitReachedPayload = (
  status: number,
  body: Record<string, unknown>,
): LimitReachedPayload | null => {
  if (status !== 429) {
    return null;
  }
  const code = typeof body.code === 'string' ? body.code : undefined;
  if (!isLimitErrorCode(code)) {
    return null;
  }

  const maxRaw = body.maxTotalTasks;
  const maxTotalTasks =
    maxRaw === null
      ? null
      : typeof maxRaw === 'number' && Number.isFinite(maxRaw)
        ? maxRaw
        : undefined;

  const maxPinMessagesRaw = body.maxPinMessages;
  const maxPinMessages =
    typeof maxPinMessagesRaw === 'number' && Number.isFinite(maxPinMessagesRaw)
      ? maxPinMessagesRaw
      : undefined;
  const maxCommentsRaw = body.maxComments;
  const maxComments =
    typeof maxCommentsRaw === 'number' && Number.isFinite(maxCommentsRaw) ? maxCommentsRaw : undefined;

  return {
    code,
    dailyLimit: typeof body.dailyLimit === 'number' ? body.dailyLimit : undefined,
    usedToday: typeof body.usedToday === 'number' ? body.usedToday : undefined,
    maxTotalTasks,
    maxPinMessages,
    maxComments,
    total: typeof body.total === 'number' ? body.total : undefined,
    retryAt: typeof body.retryAt === 'string' ? body.retryAt : undefined,
  };
};

export const parseLimitReachedFromApiClientError = (err: unknown): LimitReachedPayload | null => {
  if (!(err instanceof ApiClientError)) {
    return null;
  }
  return parseLimitReachedPayload(err.status, err.body);
};

export const isLimitReachedError = (err: unknown): boolean => {
  const fromApi = parseLimitReachedFromApiClientError(err);
  if (fromApi) {
    return true;
  }
  if (!err || typeof err !== 'object') {
    return false;
  }
  const candidate = err as { status?: unknown; body?: unknown };
  if (typeof candidate.status !== 'number' || !candidate.body || typeof candidate.body !== 'object') {
    return false;
  }
  return parseLimitReachedPayload(candidate.status, candidate.body as Record<string, unknown>) !== null;
};

type LimitReachedListener = (payload: LimitReachedPayload) => void;

let listener: LimitReachedListener | null = null;

export const registerLimitReachedListener = (fn: LimitReachedListener | null): void => {
  listener = fn;
};

export const emitLimitReached = (payload: LimitReachedPayload): void => {
  listener?.(payload);
};

export const emitLimitReachedFromHttp = (
  status: number,
  body: Record<string, unknown>,
): boolean => {
  const payload = parseLimitReachedPayload(status, body);
  if (!payload) {
    return false;
  }
  emitLimitReached(payload);
  return true;
};
